import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { DELIVERY_SLOTS } from '../data/deliveryDates';
import {
  BarChart3, Download, Search, Filter, ArrowLeft,
  FileText, Clock, CheckCircle, XCircle, Loader2,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Phone, Mail, MapPin, Send,
  Tag, Plus, Trash2, Pencil, X, Save
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700' },
  en_cours: { label: 'En cours', color: 'bg-amber-100 text-amber-700' },
  en_attente_virement: { label: 'Attente paiement', color: 'bg-green-100 text-green-700' },
  valide: { label: 'Validé', color: 'bg-green-100 text-green-700' },
  livre: { label: 'Livré', color: 'bg-emerald-100 text-emerald-800' },
  termine: { label: 'Terminé', color: 'bg-gray-100 text-gray-700' },
  annule: { label: 'Annulé', color: 'bg-red-100 text-red-700' },
};

const PRESTATION_OPTIONS = [
  { value: 'depot', label: 'Dépôt' },
  { value: 'express', label: 'Livraison Express' },
  { value: 'echange', label: 'Rotation' },
  { value: 'retrait', label: 'Retrait' },
  { value: 'passante', label: 'Chargement Express' },
];

const VOLUME_OPTIONS = ['3m³', '8m³', '10m³', '15m³', '20m³', '30m³'];
const TYPE_OPTIONS = ['Mélange (DIB)', 'Végétaux', 'Terre', 'Gravat/terre', 'Bois', 'Cartons', 'Mobilier de bureau'];
const CRENEAU_OPTIONS = DELIVERY_SLOTS;

const AdminEditModal = ({ devis, token, onClose, onSaved }) => {
  const [form, setForm] = useState({
    prestation: devis.prestation || '',
    benne_volume: devis.benne_volume || '',
    benne_type: devis.benne_type || '',
    date_debut: devis.date_debut || '',
    creneau_horaire: devis.creneau_horaire || '',
    contact_chantier: devis.contact_chantier || '',
    tel_contact_chantier: devis.tel_contact_chantier || '',
    commentaire: devis.commentaire || '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/api/admin/devis/${devis.id}?token=${token}`, form);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la modification');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="admin-edit-modal">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <Pencil className="h-5 w-5 text-green-600" />
            Modifier commande #{devis.id}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Prestation</label>
            <select value={form.prestation} onChange={e => setForm({...form, prestation: e.target.value})}
              className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="admin-edit-prestation">
              {PRESTATION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Volume</label>
              <select value={form.benne_volume} onChange={e => setForm({...form, benne_volume: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="admin-edit-volume">
                {VOLUME_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Type de déchet</label>
              <select value={form.benne_type} onChange={e => setForm({...form, benne_type: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="admin-edit-type">
                {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Date de prestation</label>
              <Input type="date" value={form.date_debut} onChange={e => setForm({...form, date_debut: e.target.value})} data-testid="admin-edit-date" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Créneau</label>
              <select value={form.creneau_horaire} onChange={e => setForm({...form, creneau_horaire: e.target.value})}
                className="w-full border rounded-lg px-3 py-2 text-sm" data-testid="admin-edit-creneau">
                {CRENEAU_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="custom">Autre</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Contact chantier</label>
              <Input value={form.contact_chantier} onChange={e => setForm({...form, contact_chantier: e.target.value})}
                placeholder="Nom" data-testid="admin-edit-contact" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Tél. contact</label>
              <Input value={form.tel_contact_chantier} onChange={e => setForm({...form, tel_contact_chantier: e.target.value})}
                placeholder="06..." data-testid="admin-edit-tel" />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 mb-1 block">Commentaire</label>
            <textarea value={form.commentaire} onChange={e => setForm({...form, commentaire: e.target.value})}
              className="w-full border rounded-lg px-3 py-2 text-sm h-20 resize-none" placeholder="Instructions..." data-testid="admin-edit-commentaire" />
          </div>

          {error && <p className="text-red-500 text-sm bg-red-50 p-3 rounded-lg" data-testid="admin-edit-error">{error}</p>}
        </div>

        <div className="flex gap-3 p-5 border-t bg-gray-50">
          <Button variant="outline" className="flex-1" onClick={onClose}>Annuler</Button>
          <Button className="flex-1 bg-green-600 hover:bg-green-700 text-white" onClick={handleSave} disabled={saving} data-testid="admin-edit-save-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [devis, setDevis] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [sendingFacture, setSendingFacture] = useState(null);
  const [showPromos, setShowPromos] = useState(false);
  const [promos, setPromos] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', discount_type: 'percent', discount_value: '', max_uses: '' });
  const [editDevis, setEditDevis] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 15;

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ token });
      if (statusFilter) params.append('status', statusFilter);
      if (search) params.append('search', search);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);

      const [devisRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/devis?${params}`),
        axios.get(`${API}/api/admin/stats?token=${token}`),
      ]);
      setDevis(devisRes.data);
      setStats(statsRes.data);
      setCurrentPage(1);
    } catch (err) {
      if (err.response?.status === 403) navigate('/mon-compte');
    }
    setLoading(false);
  }, [token, statusFilter, search, dateFrom, dateTo, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/connexion'); return; }
    fetchData();
  }, [user, authLoading, fetchData, navigate]);

  const updateStatus = async (id, newStatus) => {
    await axios.put(`${API}/api/admin/devis/${id}/status?token=${token}&new_status=${newStatus}`);
    fetchData();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/api/admin/export?token=${token}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Erreur lors de l\'export. Veuillez réessayer.');
    }
    setExporting(false);
  };

  const handleSendFacture = async (devisId) => {
    if (!window.confirm('Envoyer la facture au client ?')) return;
    setSendingFacture(devisId);
    try {
      const res = await axios.post(`${API}/api/admin/send-facture/${devisId}?token=${token}`);
      alert(res.data.message || 'Facture envoyée !');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'envoi de la facture');
    }
    setSendingFacture(null);
  };

  const handleViewFacture = (devisId) => {
    window.open(`${API}/api/facture/${devisId}?token=${token}`, '_blank');
  };

  const fetchPromos = async () => {
    setLoadingPromos(true);
    try {
      const res = await axios.get(`${API}/api/admin/promos?token=${token}`);
      setPromos(res.data);
    } catch (err) { console.error(err); }
    setLoadingPromos(false);
  };

  const handleCreatePromo = async () => {
    if (!newPromo.code || !newPromo.discount_value) return;
    try {
      await axios.post(`${API}/api/admin/promo?token=${token}&code=${newPromo.code}&discount_type=${newPromo.discount_type}&discount_value=${newPromo.discount_value}&max_uses=${newPromo.max_uses || 0}`);
      setNewPromo({ code: '', discount_type: 'percent', discount_value: '', max_uses: '' });
      fetchPromos();
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeletePromo = async (id) => {
    if (!window.confirm('Désactiver ce code promo ?')) return;
    try {
      await axios.delete(`${API}/api/admin/promo/${id}?token=${token}`);
      fetchPromos();
    } catch (err) { alert('Erreur'); }
  };

  const togglePromos = () => {
    if (!showPromos) fetchPromos();
    setShowPromos(!showPromos);
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-white border-b shadow-sm sticky top-24 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/mon-compte')} data-testid="admin-back-btn">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Dashboard Admin
            </h1>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="export-excel-btn"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
            Export Excel
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="admin-stats">
          {[
            { label: 'Total', value: stats.total, icon: FileText, color: 'text-gray-700 bg-gray-100' },
            { label: 'Nouveau', value: stats.nouveau, icon: Clock, color: 'text-blue-700 bg-blue-50' },
            { label: 'En cours', value: stats.en_cours, icon: Loader2, color: 'text-amber-700 bg-amber-50' },
            { label: 'Validé', value: stats.valide, icon: CheckCircle, color: 'text-green-700 bg-green-50' },
            { label: 'Annulé', value: stats.annule, icon: XCircle, color: 'text-red-700 bg-red-50' },
          ].map(({ label, value, icon: Icon, color }) => (
            <Card
              key={label}
              className={`p-4 cursor-pointer transition-all hover:shadow-md ${statusFilter === label.toLowerCase().replace('é', 'e') ? 'ring-2 ring-green-500' : ''}`}
              onClick={() => setStatusFilter(prev => prev === label.toLowerCase().replace('é', 'e').replace(' ', '_') ? '' : label.toLowerCase().replace('é', 'e').replace(' ', '_'))}
            >
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${color}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{value || 0}</div>
                  <div className="text-xs text-gray-500">{label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Promo Codes Section */}
        <Card className="overflow-hidden" data-testid="promo-management">
          <button onClick={togglePromos} className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-green-500" />
              <span className="font-bold text-gray-900">Codes Promo</span>
              <Badge className="bg-green-100 text-green-700 text-xs">{promos.length}</Badge>
            </div>
            {showPromos ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
          </button>

          {showPromos && (
            <div className="border-t p-4 space-y-4">
              {/* Create new promo */}
              <div className="flex flex-wrap gap-2 items-end" data-testid="promo-create-form">
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Code</label>
                  <Input
                    placeholder="EX: PROMO20"
                    value={newPromo.code}
                    onChange={e => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                    className="w-36 uppercase"
                    data-testid="promo-code-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Type</label>
                  <select
                    value={newPromo.discount_type}
                    onChange={e => setNewPromo({ ...newPromo, discount_type: e.target.value })}
                    className="border rounded-md px-3 py-2 text-sm h-10"
                    data-testid="promo-type-select"
                  >
                    <option value="percent">%</option>
                    <option value="fixed">€</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Valeur</label>
                  <Input
                    type="number"
                    placeholder="10"
                    value={newPromo.discount_value}
                    onChange={e => setNewPromo({ ...newPromo, discount_value: e.target.value })}
                    className="w-20"
                    data-testid="promo-value-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 block mb-1">Utilisations max</label>
                  <Input
                    type="number"
                    placeholder="0 = illimité"
                    value={newPromo.max_uses}
                    onChange={e => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                    className="w-32"
                    data-testid="promo-max-input"
                  />
                </div>
                <Button onClick={handleCreatePromo} className="bg-green-500 hover:bg-green-600 text-white h-10" data-testid="promo-create-btn">
                  <Plus className="h-4 w-4 mr-1" /> Créer
                </Button>
              </div>

              {/* List promos */}
              {loadingPromos ? (
                <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-green-500" /></div>
              ) : promos.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-3">Aucun code promo</p>
              ) : (
                <div className="space-y-2">
                  {promos.map(p => (
                    <div key={p.id} className={`flex items-center justify-between p-3 rounded-lg border ${p.is_active ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-100 opacity-60'}`} data-testid={`promo-item-${p.code}`}>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold text-sm text-green-600 bg-green-50 px-2 py-1 rounded">{p.code}</span>
                        <span className="text-sm text-gray-700">
                          -{p.discount_value}{p.discount_type === 'percent' ? '%' : '€'}
                        </span>
                        <span className="text-xs text-gray-400">
                          {p.used_count}/{p.max_uses || '∞'} utilisations
                        </span>
                        {!p.is_active && <Badge className="bg-red-100 text-red-600 text-xs">Désactivé</Badge>}
                      </div>
                      {p.is_active && (
                        <Button variant="ghost" size="sm" onClick={() => handleDeletePromo(p.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`promo-delete-${p.code}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Search className="h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher (nom, email, adresse, tel...)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchData()}
                className="h-9"
                data-testid="admin-search"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="h-9 rounded-md border border-gray-200 px-3 text-sm"
                data-testid="admin-status-filter"
              >
                <option value="">Tous les statuts</option>
                <option value="nouveau">Nouveau</option>
                <option value="en_cours">En cours</option>
                <option value="valide">Validé</option>
                <option value="annule">Annulé</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 w-36" data-testid="admin-date-from" />
              <span className="text-gray-400 text-sm">à</span>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 w-36" data-testid="admin-date-to" />
            </div>
            <Button size="sm" onClick={fetchData} className="bg-green-600 hover:bg-green-700 text-white h-9" data-testid="admin-apply-filter">
              Filtrer
            </Button>
          </div>
        </Card>

        {/* Devis List */}
        <div className="space-y-3" data-testid="admin-devis-list">
          {devis.length === 0 && (
            <Card className="p-8 text-center text-gray-500">
              Aucun devis trouvé
            </Card>
          )}
          {devis
            .slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
            .map((d) => {
            const isExpanded = expandedId === d.id;
            const statusInfo = STATUS_MAP[d.status] || STATUS_MAP.nouveau;
            return (
              <Card key={d.id} className="overflow-hidden" data-testid={`devis-row-${d.id}`}>
                {/* Row header */}
                <div
                  className="p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : d.id)}
                >
                  <div className="text-sm font-mono text-gray-400 w-12">#{d.id}</div>
                  <Badge className={`${statusInfo.color} text-xs`}>{statusInfo.label}</Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{d.name}</div>
                    <div className="text-xs text-gray-500 truncate">{d.adresse_chantier}</div>
                  </div>
                  <div className="hidden md:block text-sm text-gray-600">{d.prestation} {d.benne_volume}</div>
                  <div className="text-xs text-gray-400 whitespace-nowrap">{formatDate(d.created_at)}</div>
                  {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                </div>

                {/* Expanded details */}
                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-sm font-bold text-green-700">Client</h4>
                        <div className="text-sm space-y-1">
                          <div className="flex items-center gap-2"><span className="font-medium w-20">Nom:</span> {d.name}</div>
                          <div className="flex items-center gap-2"><Mail className="h-3 w-3" /> {d.email}</div>
                          <div className="flex items-center gap-2"><Phone className="h-3 w-3" /> {d.phone}</div>
                          <div className="flex items-center gap-2"><span className="font-medium w-20">Type:</span> {d.type}</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-sm font-bold text-green-700">Chantier</h4>
                        <div className="text-sm space-y-1">
                          <div className="flex items-center gap-2"><MapPin className="h-3 w-3" /> {d.adresse_chantier}</div>
                          <div><span className="font-medium">Accès / Benne:</span> {d.type_acces || '—'} / {d.type_benne_equip || '—'}</div>
                          {d.contact_chantier && <div><span className="font-medium">Contact:</span> {d.contact_chantier} {d.tel_contact_chantier && `— ${d.tel_contact_chantier}`}</div>}
                          {d.commentaire && <div><span className="font-medium">Commentaire:</span> {d.commentaire}</div>}
                        </div>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <h4 className="text-sm font-bold text-green-700">Commande</h4>
                        <div className="text-sm space-y-1">
                          <div><span className="font-medium">Produit:</span> 1 x Benne : {d.prestation} {d.benne_volume} - {d.benne_type}</div>
                          <div><span className="font-medium">Date:</span> {d.date_debut || '—'} — {d.creneau_horaire || '—'}</div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <h4 className="text-sm font-bold text-green-700">Statut</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(STATUS_MAP).map(([key, { label, color }]) => (
                            <Button
                              key={key}
                              size="sm"
                              variant={d.status === key ? "default" : "outline"}
                              className={d.status === key ? 'bg-green-600 text-white' : ''}
                              onClick={() => updateStatus(d.id, key)}
                              data-testid={`status-btn-${key}-${d.id}`}
                            >
                              {label}
                            </Button>
                          ))}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-300 text-green-600 hover:bg-green-50"
                            onClick={() => setEditDevis(d)}
                            data-testid={`edit-devis-${d.id}`}
                          >
                            <Pencil className="h-3.5 w-3.5 mr-1.5" />
                            Modifier
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-300 text-blue-700 hover:bg-blue-50"
                            onClick={() => handleViewFacture(d.id)}
                            data-testid={`view-facture-${d.id}`}
                          >
                            <FileText className="h-3.5 w-3.5 mr-1.5" />
                            Voir facture
                          </Button>
                          <Button
                            size="sm"
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleSendFacture(d.id)}
                            disabled={sendingFacture === d.id}
                            data-testid={`send-facture-${d.id}`}
                          >
                            {sendingFacture === d.id ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                            Envoyer la facture
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Pagination */}
        {devis.length > ITEMS_PER_PAGE && (
          <div className="flex items-center justify-between pt-2" data-testid="admin-pagination">
            <span className="text-sm text-gray-500">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–{Math.min(currentPage * ITEMS_PER_PAGE, devis.length)} sur {devis.length} commandes
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline" size="sm"
                disabled={currentPage === 1}
                onClick={() => { setCurrentPage(p => p - 1); setExpandedId(null); }}
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.ceil(devis.length / ITEMS_PER_PAGE) }, (_, i) => i + 1)
                .filter(p => p === 1 || p === Math.ceil(devis.length / ITEMS_PER_PAGE) || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, i) =>
                  p === '...' ? (
                    <span key={`dots-${i}`} className="px-2 text-gray-400 text-sm">...</span>
                  ) : (
                    <Button
                      key={p}
                      variant={currentPage === p ? 'default' : 'outline'}
                      size="sm"
                      className={currentPage === p ? 'bg-green-600 text-white hover:bg-green-700' : ''}
                      onClick={() => { setCurrentPage(p); setExpandedId(null); }}
                      data-testid={`pagination-page-${p}`}
                    >
                      {p}
                    </Button>
                  )
                )}
              <Button
                variant="outline" size="sm"
                disabled={currentPage >= Math.ceil(devis.length / ITEMS_PER_PAGE)}
                onClick={() => { setCurrentPage(p => p + 1); setExpandedId(null); }}
                data-testid="pagination-next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Admin Edit Modal */}
      {editDevis && (
        <AdminEditModal
          devis={editDevis}
          token={token}
          onClose={() => setEditDevis(null)}
          onSaved={() => { setEditDevis(null); fetchData(); }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
